/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Button,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_LIST_POSITION_RIGHT,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
  ButtonList,
  ButtonListProps,
  ButtonProps,
  DialogContentProps,
  TextField,
} from '@squiz/sds';
import { screenPaths } from 'apps/artificial-intelligence/src/Constants/ScreenPaths.constants';
import { AI_STRINGS } from 'apps/artificial-intelligence/src/Constants/Strings.constants';
import { replaceTenantParam } from 'apps/console-core/src/Utilities/ReplaceTenantParam/ReplaceTenantParam';
import React, { ChangeEvent, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  SemanticAuditorCreateTestDialogErrorTypes,
  SemanticAuditorTestDialogErrors,
} from './SemanticAuditorCreateTestDialogContent.defs';

export const SemanticAuditorCreateTestDialogContent = ({ close }: DialogContentProps): JSX.Element => {
  const [testName, setTestName] = useState('');
  const [error, setError] = useState<SemanticAuditorTestDialogErrors | null>(null);
  const id = useId();
  const { t } = useTranslation();
  const labels = {
    cancel: t(AI_STRINGS.cancel),
    create: t(AI_STRINGS.create),
    name: t(AI_STRINGS.name),
    nameAlreadyInUse: t(AI_STRINGS.nameAlreadyInUse),
    provideTestName: t(AI_STRINGS.provideTestName),
    requiredField: t(AI_STRINGS.requiredField),
    somethingWentWrong: t(AI_STRINGS.somethingWentWrong),
  };
  const navigate = useNavigate();

  // TODO: add POST API call to create a test, get an actual ID
  const onCreateHandler = (): void => {
    if (!testName) {
      return setError(SemanticAuditorCreateTestDialogErrorTypes.EMPTY_NAME);
    }
    const id = '1234';
    navigate(replaceTenantParam(screenPaths.semanticAuditorTestBuilder.replace(':id', id)));
  };

  // TODO: Cover all cases once we've got a service set up
  // istanbul ignore next
  const errorMessage = useMemo(() => {
    switch (error) {
      case SemanticAuditorCreateTestDialogErrorTypes.EMPTY_NAME:
        return labels.requiredField;
      case SemanticAuditorCreateTestDialogErrorTypes.DUPLICATE_NAME:
        return labels.nameAlreadyInUse;
      case SemanticAuditorCreateTestDialogErrorTypes.REQUEST_ERROR:
        return labels.somethingWentWrong;
      default:
        return undefined;
    }
  }, [error, labels.nameAlreadyInUse, labels.requiredField, labels.somethingWentWrong]);

  const createBtnProps: ButtonProps = {
    classes: 'semantic-auditor-create-test-dialog__create-btn',
    format: BUTTON_FORMAT_STRONG,
    onClick: onCreateHandler,
    text: labels.create,
    theme: BUTTON_THEME_SUCCESS,
  };

  const cancelBtnProps: ButtonProps = {
    classes: 'semantic-auditor-create-test-dialog__cancel-btn',
    format: BUTTON_FORMAT_SOFT,
    onClick: close,
    text: labels.cancel,
    theme: BUTTON_THEME_DEFAULT,
  };

  const buttonListProps: ButtonListProps = {
    buttonKeys: ['create', 'cancel'],
    buttons: [
      <Button {...createBtnProps} key={'dialog-create-test__create-btn'} />,
      <Button {...cancelBtnProps} key={'dialog-create-test__cancel-btn'} />,
    ],
    buttonsPosition: BUTTON_LIST_POSITION_RIGHT,
  };
  return (
    <div className="semantic-auditor-create-test-dialog__content">
      <p className="semantic-auditor-create-test-dialog__help-text">{labels.provideTestName}</p>
      <TextField
        classes="semantic-auditor-create-test-dialog__name-field"
        errorMessage={errorMessage}
        fieldLabel={labels.name}
        id={id}
        name={labels.name}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setTestName(e.target.value)}
        required
        value={testName}
      />
      <ButtonList {...buttonListProps} />
    </div>
  );
};
